import React from "react";
import { Card } from "@shadcn-in-nx/ui/card";
import { Badge } from "@shadcn-in-nx/ui/badge";
import { Checkbox } from "@shadcn-in-nx/ui/checkbox";
import { Button } from "@shadcn-in-nx/ui/button";
import { Clock, User, Building, ChevronRight, MessageSquare, Tag, Archive, Eye } from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import { useManageCompany } from "../ManageCompany/ManageCompanyContext";

const statusColors = {
    OPEN: "bg-blue-100 text-blue-700",
    IN_PROGRESS: "bg-yellow-100 text-yellow-700",
    WAITING_ON_CUSTOMER: "bg-purple-100 text-purple-700",
    RESOLVED: "bg-green-100 text-green-700",
    CLOSED: "bg-gray-100 text-gray-700",
};

const priorityColors = {
    LOW: "bg-gray-100 text-gray-600",
    NORMAL: "bg-blue-100 text-blue-600",
    HIGH: "bg-orange-100 text-orange-600",
    URGENT: "bg-red-100 text-red-600",
};

export default function TicketListItem({ ticket, isSelected, onToggle, onNavigate, companies, isLastViewed, lastViewedAt, isHighlighted }) {
    const companyName = companies[ticket.companyId] || "";
    const email = ticket.createdByEmail || ticket.senderEmail;
    const { open: openManageCompany } = useManageCompany();

    const highlightCls = isHighlighted ? "ring-2 ring-blue-500 ring-offset-1 shadow-md" : isLastViewed ? "ring-2 ring-emerald-400 bg-emerald-50/50" : lastViewedAt ? "border-l-4 border-l-blue-300" : "";

    return (
        <Card id={`support-ticket-row-${ticket.id}`} className={`p-3 sm:p-4 hover:shadow-md transition-shadow cursor-pointer ${highlightCls}`}>
            <div className="flex items-start gap-2 sm:gap-3">
                <div className="pt-1 hidden sm:block" onClick={(e) => e.stopPropagation()}>
                    <Checkbox checked={isSelected} onCheckedChange={() => onToggle(ticket.id)} />
                </div>
                <div className="flex-1 min-w-0" onClick={() => onNavigate(ticket.id)}>
                    <div className="flex items-center gap-1.5 sm:gap-2 mb-1 flex-wrap">
                        <span className="text-xs text-muted-foreground font-mono">#{ticket.id.slice(0, 8)}</span>
                        {ticket.archivedAt && (
                            <Badge variant="secondary" className="bg-gray-200 text-gray-600 text-[10px] sm:text-xs">
                                <Archive className="w-3 h-3 mr-0.5 sm:mr-1" />Archived
                            </Badge>
                        )}
                        <Badge className={`${statusColors[ticket.status] || statusColors.OPEN} text-[10px] sm:text-xs`}>
                            {ticket.status?.replace(/_/g, " ")}
                        </Badge>
                        <Badge variant="outline" className={`${priorityColors[ticket.priority] || priorityColors.NORMAL} text-[10px] sm:text-xs`}>
                            {ticket.priority}
                        </Badge>
                    </div>
                    <h3 className="font-medium truncate text-sm sm:text-base">{ticket.subject}</h3>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 sm:mt-2 text-xs sm:text-sm text-muted-foreground">
                        {companyName && (
                            <span className="flex items-center gap-1">
                                <Building className="w-3 h-3" />
                                <span className="truncate max-w-[120px] sm:max-w-[150px]">{companyName}</span>
                            </span>
                        )}
                        {email && (
                            <span className="flex items-center gap-1 hidden sm:flex">
                                <User className="w-3 h-3" />
                                <span className="truncate max-w-[150px]">{email}</span>
                            </span>
                        )}
                        <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            <span>{format(new Date(ticket.createdAt), "MMM d")}</span>
                        </span>
                        {ticket.messageCount > 0 && (
                            <span className="flex items-center gap-1">
                                <MessageSquare className="w-3 h-3" />
                                <span>{ticket.messageCount}</span>
                            </span>
                        )}
                        {lastViewedAt && (
                            <span className="hidden sm:flex items-center gap-1 text-emerald-600">
                                <Eye className="w-3 h-3" />
                                <span>{formatDistanceToNow(new Date(lastViewedAt), { addSuffix: true })}</span>
                            </span>
                        )}
                    </div>
                    {ticket.tags?.length > 0 && (
                        <div className="flex items-center gap-1 mt-1.5 sm:mt-2 flex-wrap">
                            <Tag className="w-3 h-3 text-muted-foreground" />
                            {ticket.tags.slice(0, 3).map((tag) => (
                                <Badge key={tag} variant="outline" className="text-[10px] sm:text-xs py-0">{tag}</Badge>
                            ))}
                            {ticket.tags.length > 3 && (
                                <span className="text-xs text-muted-foreground">+{ticket.tags.length - 3}</span>
                            )}
                        </div>
                    )}
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    {ticket.companyId && (
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            title="Manage company"
                            onClick={(e) => { e.stopPropagation(); openManageCompany(ticket.companyId); }}
                        >
                            <Building className="w-4 h-4 text-muted-foreground" />
                        </Button>
                    )}
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                </div>
            </div>
        </Card>
    );
}
